import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Timer as TimerIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Videocam as VideocamIcon,
  Mic as MicIcon
} from '@mui/icons-material';
import { socket } from '../socket';
import { API_ENDPOINTS, apiRequest } from '../config/api';

// Import existing components
import ExamQuestions from './ExamQuestions';
import WebcamStream from './WebcamStream';
import AudioMonitor from './AudioMonitor';
import AlertDisplay from './AlertDisplay';
import WarningPopup from './WarningPopup';

const IntegratedExamInterface = ({ examId, onExamComplete, onReturnToDashboard }) => {
  const [examStarted, setExamStarted] = useState(false);
  const [examData, setExamData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [warningCount, setWarningCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [proctorPaused, setProctorPaused] = useState(false);
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [webcamPermission, setWebcamPermission] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submissions
  const [micPermission, setMicPermission] = useState(false);
  const [permissionsChecked, setPermissionsChecked] = useState(false);

  // Proctoring state - exactly like original proctor app
  const [warningPopupOpen, setWarningPopupOpen] = useState(false);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [examTerminated, setExamTerminated] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [currentAlertType, setCurrentAlertType] = useState('');
  const [alertQueue, setAlertQueue] = useState([]);
  const [permissionLostDialog, setPermissionLostDialog] = useState(false);
  const [examPaused, setExamPaused] = useState(false);

  // Refs for cooldown and popup management - exactly like original
  const lastPopupTimeRef = useRef(0);
  const lastAlertMessageRef = useRef('');
  const POPUP_COOLDOWN = 3000; // 3 seconds cooldown like original

  const examQuestionsRef = useRef(null);
  const webcamRef = useRef(null);
  const timerRef = useRef(null);
  const socketRef = useRef(null);

  const maxWarnings = 10; // Same as original proctor app

  useEffect(() => {
    // Check permissions when component mounts
    checkPermissions();

    // Fetch exam data
    fetchExamData();

    // Initialize socket connection for proctoring - exactly like original proctor app
    initializeSocket();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Clean up socket connection
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [examId]);

  // Focus change detection - exactly like original proctor app
  useEffect(() => {
    if (!examStarted) return;

    const handleVisibilityChange = () => {
      const isHidden = document.hidden;
      if (isHidden && examStarted) {
        // Document is hidden (user switched tabs or minimized window)
        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit('focus_change', { is_focused: false });
        }
      } else {
        // Document is visible
        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit('focus_change', { is_focused: true });
        }
      }
    };

    const handleBlur = () => {
      if (examStarted && socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('focus_change', { is_focused: false });
      }
    };

    const handleFocus = () => {
      if (examStarted && socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('focus_change', { is_focused: true });
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      // Clean up event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [examStarted]);

  // Prevent browser reload notifications - exactly like previous app
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Don't show reload notification during exam
      if (examStarted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [examStarted]);

  // Monitor permission changes during active exam
  useEffect(() => {
    if (!examStarted) return;

    // Check if permissions are lost during exam
    if (examStarted && (!webcamPermission || !micPermission)) {
      console.log('🚨 PERMISSION LOST DURING EXAM!');
      console.log('🚨 Camera permission:', webcamPermission);
      console.log('🚨 Microphone permission:', micPermission);

      // Pause the exam immediately
      setExamPaused(true);
      setPermissionLostDialog(true);

      // Pause the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Send alert to backend about permission loss
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('permission_lost', {
          exam_id: examId,
          camera_permission: webcamPermission,
          microphone_permission: micPermission,
          timestamp: new Date().toISOString()
        });
      }
    } else if (examStarted && webcamPermission && micPermission && examPaused) {
      // Permissions restored - resume exam
      console.log('✅ PERMISSIONS RESTORED - RESUMING EXAM');
      setExamPaused(false);
      setPermissionLostDialog(false);

      // Resume the timer
      startExamTimer();
    }
  }, [webcamPermission, micPermission, examStarted, examPaused]);

  const initializeSocket = () => {
    try {
      // Get user and exam information for socket connection
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const userId = user ? user.username : 'unknown';

      console.log('🔌 Initializing socket with:', { userId, examId });
      console.log('🔌 localStorage user:', user);
      console.log('🔌 examId from props:', examId);

      // Use the shared socket from socket.js instead of creating a new one
      // This prevents conflicts between IntegratedExamInterface and WebcamStream
      socketRef.current = socket;

      // Update socket query parameters for this exam session
      if (socket.io && socket.io.opts) {
        socket.io.opts.query = {
          user_id: userId,
          exam_id: examId
        };
      }

      // Ensure socket is connected
      if (!socket.connected) {
        socket.connect();
      }

      socketRef.current.on('connect', () => {
        console.log('🔌 Socket connected for proctoring');
        console.log('🔌 Socket ID:', socketRef.current.id);
      });

      socketRef.current.on('disconnect', () => {
        console.log('🔌 Socket disconnected');
      });

      socketRef.current.on('connect_error', (error) => {
        console.error('🔌 Socket connection error:', error);
      });

      // Listen for proctoring alerts from backend - exactly like original proctor app
      socketRef.current.on('alert', (data) => {
        console.log('🚨🚨🚨 ALERT RECEIVED FROM BACKEND:', data);
        console.log('🚨🚨🚨 Alert type:', data.type);
        console.log('🚨🚨🚨 Alert message:', data.message);
        console.log('🚨🚨🚨 Current exam started:', examStarted);
        addAlert({
          message: data.message,
          type: data.type || 'warning',
          timestamp: data.timestamp || new Date().toISOString(),
          warning_count: data.warning_count,
          max_warnings: data.max_warnings
        });
      });

      socketRef.current.on('critical_alert', (data) => {
        console.log('🚨🚨🚨 CRITICAL ALERT RECEIVED FROM BACKEND:', data);
        console.log('🚨🚨🚨 Critical alert type:', data.type);
        console.log('🚨🚨🚨 Critical alert message:', data.message);
        console.log('🚨🚨🚨 Critical alert warning_count:', data.warning_count);
        addAlert({
          message: data.message,
          type: data.type || 'FOCUS', // Focus loss is the main critical alert
          timestamp: data.timestamp || new Date().toISOString(),
          warning_count: data.warning_count, // This ensures focus alerts count towards 10 warnings
          max_warnings: data.max_warnings
        });
      });

      // Keep the old listener for backward compatibility
      socketRef.current.on('proctoring_alerts', (data) => {
        console.log('Proctoring alert received (legacy):', data);
        if (data.alerts && data.alerts.length > 0) {
          data.alerts.forEach(alert => addAlert(alert));
        }
      });

      socketRef.current.on('exam_terminated', (data) => {
        console.log('Exam terminated:', data);
        setExamTerminated(true);
        setWarningMessage(data.message);
        setWarningPopupOpen(true);
        setCurrentAlertType('error');

        // If exam was auto-submitted, mark it as submitted
        if (data.auto_submitted) {
          console.log('🚫 Exam was automatically submitted upon termination');
          setExamSubmitted(true);

          // Create a submission result for the terminated exam
          // Get current exam data from ExamQuestions component if available
          let currentExamData = {};
          if (examQuestionsRef.current) {
            currentExamData = examQuestionsRef.current.getExamData();
          }

          const terminatedResult = {
            status: 'success',
            message: 'Exam terminated and automatically submitted',
            exam_id: examId,
            submission_id: 'auto_terminated',
            score: 0, // Will be calculated by backend based on correct answers
            answers: currentExamData.answers || {},
            questions_with_answers: [], // Will be populated by backend
            total_questions: currentExamData.questions?.length || 0,
            status_type: 'terminated',
            termination_reason: 'Maximum warnings reached',
            warning_count: data.warning_count
          };

          setSubmissionResult(terminatedResult);

          // Stop the timer
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }

          // Stop proctoring
          stopProctoring();
        }
      });

    } catch (error) {
      console.error('Error initializing socket:', error);
    }
  };

  const checkPermissions = async () => {
    try {
      // Check camera permissions with real-time monitoring
      if (navigator.permissions && navigator.permissions.query) {
        // Monitor camera permissions
        const cameraPermission = await navigator.permissions.query({ name: 'camera' });
        console.log(`Camera permission status: ${cameraPermission.state}`);

        if (cameraPermission.state === 'granted') {
          setWebcamPermission(true);
        } else {
          setWebcamPermission(false);
        }

        // Listen for camera permission changes - exactly like previous app
        cameraPermission.onchange = () => {
          console.log(`Camera permission changed to: ${cameraPermission.state}`);
          if (cameraPermission.state === 'granted') {
            setWebcamPermission(true);
          } else {
            setWebcamPermission(false);
          }
        };

        // Monitor microphone permissions
        const micPermission = await navigator.permissions.query({ name: 'microphone' });
        console.log(`Microphone permission status: ${micPermission.state}`);

        if (micPermission.state === 'granted') {
          setMicPermission(true);
        } else {
          setMicPermission(false);
        }

        // Listen for microphone permission changes - exactly like previous app
        micPermission.onchange = () => {
          console.log(`Microphone permission changed to: ${micPermission.state}`);
          if (micPermission.state === 'granted') {
            setMicPermission(true);
          } else {
            setMicPermission(false);
          }
        };

        console.log('✅ Permission monitoring set up');
      } else {
        // Fallback for browsers without permissions API
        setWebcamPermission(false);
        setMicPermission(false);
        console.log('📷 Permissions API not supported - will request when starting exam');
      }
    } catch (error) {
      console.error('Permission check failed:', error);
      // Default to false, will request when needed
      setWebcamPermission(false);
      setMicPermission(false);
    } finally {
      setPermissionsChecked(true);
    }
  };

  const requestPermissions = async () => {
    try {
      // Request both camera and microphone permissions together
      // This will trigger the browser's native permission popup
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });

      // If we get here, both permissions were granted
      setWebcamPermission(true);
      setMicPermission(true);

      // Stop the stream immediately as we only needed it for permission
      stream.getTracks().forEach(track => track.stop());

      console.log('✅ Camera and microphone permissions granted');
      return true;
    } catch (error) {
      console.error('Permission request failed:', error);
      setWebcamPermission(false);
      setMicPermission(false);
      return false;
    }
  };

  const handleRestorePermissions = async () => {
    console.log('🔄 Attempting to restore permissions...');
    const granted = await requestPermissions();
    if (granted) {
      console.log('✅ Permissions restored successfully');
      // The useEffect will handle resuming the exam
    } else {
      console.log('❌ Failed to restore permissions');
    }
  };

  const fetchExamData = async () => {
    try {
      // Get exam details from backend using the persistent exam ID
      const response = await apiRequest(API_ENDPOINTS.EXAM_DETAILS(examId));
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setExamData(data.exam);

          // Set initial time remaining to full exam duration (in seconds)
          // Timer will only start when student clicks "Start Exam"
          const examDurationInSeconds = data.exam.duration * 60;
          setTimeRemaining(examDurationInSeconds);
          console.log(`Exam duration set to: ${data.exam.duration} minutes (${examDurationInSeconds} seconds)`);
        } else {
          console.error('Failed to fetch exam data:', data.message);
        }
      } else {
        console.error('Failed to fetch exam data: HTTP', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch exam data:', error);
    }
  };

  const startExamTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleStartExam = async () => {
    console.log('🎯 IntegratedExamInterface: handleStartExam called for exam:', examId);

    // Check permissions before starting exam
    if (!webcamPermission || !micPermission) {
      console.log('❌ Cannot start exam - permissions not granted');
      return;
    }

    // Additional check: verify with backend that this exam can be started
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const userId = user?.username || localStorage.getItem('userId') || 'unknown';

      console.log('🔍 Checking if exam can be started for user:', userId);

      // Check with backend if exam was already submitted
      const response = await fetch(`${API_ENDPOINTS.EXAM_SUBMISSIONS}?exam_id=${examId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        const existingSubmission = data.submissions?.find(sub => {
          const isThisStudent = sub.student_username === userId || sub.user_id === userId;
          const isSubmitted = sub.status === 'completed' || sub.status === 'terminated';
          return isThisStudent && isSubmitted;
        });

        if (existingSubmission) {
          console.log('🚫 Exam already submitted, cannot start:', existingSubmission);
          alert('This exam has already been submitted and cannot be retaken.');
          onReturnToDashboard();
          return;
        }
      }
    } catch (error) {
      console.error('Error checking exam submission status:', error);
    }

    // Start proctoring session - exactly like original proctor app
    if (socketRef.current && socketRef.current.connected) {
      // Get user information from localStorage with multiple fallbacks
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const userId = user?.username || localStorage.getItem('userId') || localStorage.getItem('studentId') || 'unknown';
      const studentName = user?.name || localStorage.getItem('studentName') || 'Unknown Student';

      console.log('🚀🚀🚀 STARTING PROCTORING SESSION');
      console.log('🚀🚀🚀 Socket connected:', socketRef.current.connected);
      console.log('🚀🚀🚀 Exam ID:', examId);
      console.log('🚀🚀🚀 User ID:', userId);
      console.log('🚀🚀🚀 Student Name:', studentName);
      console.log('🚀🚀🚀 User object:', user);

      socketRef.current.emit('start_proctoring', {
        exam_id: examId,
        user_id: userId,
        student_name: studentName
      });

      console.log('🚀🚀🚀 Started proctoring session for exam:', examId);
    } else {
      console.error('🚀🚀🚀 SOCKET NOT CONNECTED - Cannot start proctoring');
      console.error('🚀🚀🚀 Socket ref:', socketRef.current);
      console.error('🚀🚀🚀 Socket connected:', socketRef.current?.connected);
    }

    // Reset warnings and alerts - exactly like original proctor app
    setWarningCount(0);
    setAlerts([]);
    setAlertQueue([]);
    localStorage.removeItem('warningCount');

    // Set exam as started and start the timer
    setExamStarted(true);

    // Start timer with full exam duration - timer starts NOW when student clicks "Start Exam"
    console.log(`⏰ Starting exam timer with ${timeRemaining} seconds (${Math.floor(timeRemaining / 60)} minutes)`);
    startExamTimer();
  };

  const handleTimeUp = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    console.log('⏰ Time expired - automatically submitting exam without confirmation');

    // Auto-submit the exam without confirmation dialog
    if (examQuestionsRef.current) {
      examQuestionsRef.current.autoSubmitExam();
    }
  };

  const handleExamSubmission = async (submissionData) => {
    // Prevent double submissions
    if (isSubmitting || examSubmitted) {
      console.log('Submission already in progress or completed');
      return;
    }

    setIsSubmitting(true);
    console.log('Starting exam submission...');
    console.log('Submission data received:', submissionData);

    try {
      // Ensure we use the persistent exam ID
      const finalSubmissionData = {
        ...submissionData,
        exam_id: examId, // Use the persistent exam ID
        student_username: localStorage.getItem('userId'),
        submitted_at: new Date().toISOString(),
        proctoring_warnings: warningCount,
        proctoring_alerts: alerts
      };

      const response = await apiRequest(API_ENDPOINTS.EXAM_SUBMIT, {
        method: 'POST',
        body: JSON.stringify(finalSubmissionData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Backend response:', result);

        // Store both backend result and original submission data for statistics
        const enhancedResult = {
          ...result,
          ...submissionData, // Include original submission data
          questions_with_answers: submissionData.questions_with_answers || []
        };

        console.log('Enhanced result for statistics:', enhancedResult);
        setSubmissionResult(enhancedResult);
        setExamSubmitted(true);
        setShowSubmissionDialog(true);

        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        // Stop proctoring when exam is submitted
        stopProctoring();
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Exam submission error:', error);
      alert('Failed to submit exam. Please try again.');
    } finally {
      // Always reset submission flag when done (success or error)
      setIsSubmitting(false);
    }
  };

  // Helper function to determine if alert should show popup - exactly like original
  const shouldDisplayAsPopup = (alert) => {
    // Always show critical alerts
    if (alert.type === 'CRITICAL' || alert.type === 'error') {
      return true;
    }

    // Show warnings but with cooldown - INCLUDING FOCUS and SPEECH alerts
    if (alert.type === 'warning' || alert.type === 'AUDIO' || alert.type === 'GAZE' ||
        alert.type === 'PERSON' || alert.type === 'PHONE' || alert.type === 'SPOOF' ||
        alert.type === 'FOCUS' || alert.type === 'SPEECH') {
      return true;
    }

    return false;
  };

  const addAlert = (alert) => {
    // Don't process new alerts if proctoring is paused due to an active warning
    if (proctorPaused && !alert.type.includes('CRITICAL')) {
      console.log('🚫 Alert ignored - proctoring paused:', alert);
      return;
    }

    console.log('🚨🚨🚨 ALERT RECEIVED IN INTEGRATED INTERFACE:', alert);
    console.log('🚨🚨🚨 Alert type:', alert.type);
    console.log('🚨🚨🚨 Alert message:', alert.message);
    console.log('🚨🚨🚨 Current warningPopupOpen state:', warningPopupOpen);
    console.log('🚨🚨🚨 Current examStarted state:', examStarted);

    // Check cooldown period - exactly like original
    const now = Date.now();
    if (now - lastPopupTimeRef.current < POPUP_COOLDOWN && alert.type !== 'FOCUS') {
      console.log(`Alert ignored due to ${POPUP_COOLDOWN/1000}s cooldown period:`, alert.message);
      return;
    }

    // Custom messages for different alert types - exactly like original
    let customMessage = alert.message || "Warning: Suspicious activity detected.";

    // Gaze tracking alerts
    if (alert.type === 'GAZE' || (alert.message && alert.message.includes('gaze'))) {
      customMessage = "👀 Warning: Looking away from screen detected. Please keep your eyes on the exam screen to avoid disqualification.";
    }

    // Audio/speech detection
    else if (alert.type === 'AUDIO' || alert.type === 'SPEECH' || alert.subType === 'SPEECH' ||
            (alert.message && (alert.message.includes('Speech') ||
                              alert.message.includes('speaking') ||
                              alert.message.includes('mouth') ||
                              alert.message.includes('talking')))) {
      customMessage = "⚠️ Warning: Voice detected. You are not allowed to speak during the exam. Stay silent to avoid disqualification.";
    }

    // Mobile phone detection
    else if (alert.type === 'PHONE' || (alert.message && alert.message.includes('phone'))) {
      customMessage = "🚫 Warning: Mobile phone detected in the camera frame. Using electronic devices during the exam is strictly prohibited and may result in termination.";
    }

    // Person not detected
    else if (alert.type === 'PERSON' || (alert.message && alert.message.includes('No person'))) {
      customMessage = "❗ Warning: Candidate not detected in the camera. Please ensure your face is clearly visible to continue the exam.";
    }

    // Focus loss detection
    else if (alert.type === 'FOCUS' || (alert.message && (alert.message.includes('focus') || alert.message.includes('tab') || alert.message.includes('window')))) {
      customMessage = "🔍 Warning: Focus lost detected. You switched tabs or left the exam window. Please stay focused on the exam to avoid disqualification.";
    }

    // Handle warning count - exactly like original
    if (alert.warning_count !== undefined) {
      console.log("🔢 Using backend warning count:", alert.warning_count);
      console.log("🔢 Alert type:", alert.type);
      console.log("🔢 Alert message:", alert.message);
      setWarningCount(alert.warning_count);
      localStorage.setItem('warningCount', alert.warning_count.toString());
    } else {
      console.log("🔢 Incrementing warning count locally for alert:", alert.type);
      console.log("🔢 Alert message:", alert.message);
      setWarningCount(prevCount => {
        const newCount = prevCount + 1;
        console.log("🔢 New warning count:", newCount, "/", maxWarnings);
        localStorage.setItem('warningCount', newCount.toString());

        // Check if max warnings reached - only at exactly 10th warning
        if (newCount === maxWarnings) {
          console.log('🔢 Maximum warnings reached, showing termination popup');
          setExamTerminated(true);
          setWarningMessage(`Your exam has been terminated due to reaching the maximum number of warnings (${maxWarnings}). Click 'Return to Dashboard' to submit your exam.`);
          setWarningPopupOpen(true);
          setCurrentAlertType('error');

          // Don't auto-submit - require user interaction
          // The termination dialog will handle submission when user clicks the button
        } else if (newCount > maxWarnings) {
          // Already terminated, don't show more warnings
          console.log('🚫 Exam already terminated, ignoring additional violations');
          return newCount;
        }

        return newCount;
      });
    }

    // Create customized alert - exactly like original
    const customizedAlert = {
      ...alert,
      message: customMessage,
      max_warnings: alert.max_warnings || maxWarnings
    };

    // Add to alerts list for history
    setAlerts(prevAlerts => [...prevAlerts, customizedAlert]);

    // Update last alert message
    lastAlertMessageRef.current = customMessage;

    // Check if this alert should trigger a popup
    const shouldShowPopup = shouldDisplayAsPopup(customizedAlert);

    console.log('🚨 Should show popup:', shouldShowPopup);
    console.log('🚨 Customized alert:', customizedAlert);

    if (!shouldShowPopup) {
      console.log("Alert filtered from popup display:", customMessage);
      return;
    }

    // Process immediately if no popup is currently shown - exactly like original
    if (!warningPopupOpen) {
      console.log('🚨 SHOWING WARNING POPUP:', customMessage);
      console.log('🚨 Alert type:', alert.type);
      console.log('🚨 Current warning count:', warningCount);
      console.log('🚨 This popup WILL COUNT towards 10 warning limit');
      setWarningMessage(customMessage);
      setWarningPopupOpen(true);
      setCurrentAlertType(alert.type);
      setProctorPaused(true); // Pause proctoring while warning is shown
      lastPopupTimeRef.current = now;
    } else {
      console.log('🚨 ADDING TO QUEUE - popup already open');
      console.log('🚨 Queued alert type:', alert.type);
      console.log('🚨 Queued alert WILL COUNT when displayed');
      // Add to queue for later display - exactly like original
      setAlertQueue(prevQueue => {
        if (prevQueue.some(queuedAlert => queuedAlert.message === customMessage)) {
          return prevQueue;
        }
        return [...prevQueue, customizedAlert];
      });
    }
  };

  const handleWarningPopupClose = () => {
    console.log("Closing warning popup");
    setWarningPopupOpen(false);
    setProctorPaused(false); // Resume proctoring when warning is acknowledged

    // Process next alert in queue if any - exactly like original
    if (alertQueue.length > 0) {
      const nextAlert = alertQueue[0];
      setAlertQueue(prevQueue => prevQueue.slice(1));

      // Show the next alert after a brief delay
      setTimeout(() => {
        setWarningMessage(nextAlert.message);
        setWarningPopupOpen(true);
        setCurrentAlertType(nextAlert.type);
        setProctorPaused(true); // Pause proctoring for next warning
        lastPopupTimeRef.current = Date.now();
      }, 500);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const stopProctoring = () => {
    console.log("Stopping all proctoring activities...");

    // Stop socket.io proctoring on the backend
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('stop_proctoring');
    }

    // Set exam state to inactive
    setExamStarted(false);

    // Reset alerts and warning count
    setAlerts([]);
    setWarningCount(0);
    setWarningPopupOpen(false);
    setCurrentAlert(null);
  };

  const handleReturnToDashboard = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Stop proctoring when returning to dashboard
    stopProctoring();

    onReturnToDashboard();
  };

  if (!permissionsChecked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Checking permissions...</Typography>
      </Box>
    );
  }

  if (examSubmitted) {
    // Calculate detailed statistics
    const calculateDetailedStats = () => {
      console.log('Calculating stats with submissionResult:', submissionResult);

      if (!submissionResult) {
        console.log('No submission result available');
        return {
          attemptedCount: 0,
          notAttemptedCount: 0,
          totalQuestions: 0
        };
      }

      const totalQuestions = submissionResult.total_questions || 0;
      console.log('Total questions:', totalQuestions);

      // Try multiple ways to get the attempted count
      let attemptedCount = 0;

      // Method 1: Use questions_with_answers if available
      if (submissionResult.questions_with_answers && Array.isArray(submissionResult.questions_with_answers)) {
        console.log('Using questions_with_answers array');
        submissionResult.questions_with_answers.forEach(q => {
          const userAnswer = q.user_answer || q.student_answer || '';
          if (userAnswer && userAnswer.trim() !== '') {
            attemptedCount++;
          }
        });
      }
      // Method 2: Use answers object if available
      else if (submissionResult.answers && typeof submissionResult.answers === 'object') {
        console.log('Using answers object');
        Object.values(submissionResult.answers).forEach(answer => {
          if (answer && answer.trim() !== '') {
            attemptedCount++;
          }
        });
      }
      // Method 3: Fallback to any available count
      else if (submissionResult.attempted_questions !== undefined) {
        attemptedCount = submissionResult.attempted_questions;
      }

      const notAttemptedCount = totalQuestions - attemptedCount;

      console.log('Final stats:', { attemptedCount, notAttemptedCount, totalQuestions });

      return {
        attemptedCount,
        notAttemptedCount,
        totalQuestions
      };
    };

    const stats = calculateDetailedStats();

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 400, width: '100%', borderRadius: 2 }}>
          {/* Success icon */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#4caf50',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                fontSize: '40px',
                fontWeight: 'bold'
              }}
            >
              ✓
            </Box>
          </Box>

          {submissionResult?.status_type === 'terminated' ? (
            <Typography variant="h5" gutterBottom sx={{ color: '#f44336', fontWeight: 'bold', mb: 4 }}>
              Exam Terminated
            </Typography>
          ) : (
            <Typography variant="h5" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold', mb: 4 }}>
              Exam Submitted Successfully
            </Typography>
          )}

          {submissionResult && (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'left', mb: 2 }}>
                Exam Statistics:
              </Typography>

              <Box sx={{ textAlign: 'left', mb: 4 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Questions attempted:</strong> {stats.attemptedCount} out of {stats.totalQuestions}
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Questions not attempted:</strong> {stats.notAttemptedCount} out of {stats.totalQuestions}
                </Typography>
              </Box>
            </>
          )}

          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handleReturnToDashboard}
          >
            RETURN TO DASHBOARD
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!examStarted) {
    return (
      <Box sx={{ height: '100vh', bgcolor: '#f5f5f5', overflow: 'auto' }}>
        {/* Blue Header - Like Original Proctor App */}
        <Paper sx={{
          bgcolor: '#1976d2',
          color: 'white',
          p: 2,
          borderRadius: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {examData?.title || 'Exam'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReturnToDashboard}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                },
                mr: 2
              }}
            >
              BACK TO DASHBOARD
            </Button>
            <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: '50%' }}></Box>
            <Box sx={{ width: 12, height: 12, bgcolor: '#ff9800', borderRadius: '50%' }}></Box>
            <Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: '50%' }}></Box>
            <Button
              variant="contained"
              size="small"
              sx={{
                ml: 2,
                bgcolor: webcamPermission && micPermission ? '#4caf50' : '#9e9e9e',
                '&:hover': {
                  bgcolor: webcamPermission && micPermission ? '#45a049' : '#9e9e9e'
                },
                fontWeight: 'bold',
                cursor: webcamPermission && micPermission ? 'pointer' : 'not-allowed'
              }}
              onClick={handleStartExam}
              disabled={!webcamPermission || !micPermission}
            >
              START EXAM
            </Button>
          </Box>
        </Paper>

        {/* Full Screen Exam Instructions */}
        <Box sx={{ p: 0 }}>
          <Paper sx={{ p: 4, borderRadius: 0 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
              Exam Instructions
            </Typography>

            {/* Exam Overview */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', mb: 3 }}>
                📋 Exam Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <strong>Exam Title:</strong> {examData?.title || 'Exam'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <strong>Duration:</strong> {examData?.duration || 30} minutes
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <strong>Time Remaining:</strong> {formatTime(timeRemaining)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={4}>
              {/* Question Format */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3, border: '2px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                  <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', mb: 3 }}>
                    📝 Question Format
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
                    • <strong>Total Questions:</strong> {examData?.num_questions || 50}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
                    • <strong>Question Type:</strong> Multiple Choice Questions (MCQ)
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
                    • <strong>Marks per Question:</strong> 2 marks
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                    • <strong>Total Marks:</strong> 100 marks
                  </Typography>
                </Box>
              </Grid>

              {/* Time Management */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3, border: '2px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                  <Typography variant="h5" gutterBottom sx={{ color: '#ff9800', display: 'flex', alignItems: 'center', mb: 3 }}>
                    ⏰ Time Management
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
                    • <strong>Time Limit:</strong> {examData?.duration || 30} minutes
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', color: '#ff9800', fontWeight: 'bold' }}>
                    • <strong>Timer starts when you click "Start Exam"</strong>
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
                    • Manage your time wisely
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
                    • You can navigate between questions freely
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                    • Review your answers before submission
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Proctoring Warning */}
            <Alert severity="warning" sx={{ mt: 4, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                IMPORTANT: Proctoring Guidelines
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                This exam is proctored. Your camera and microphone will monitor your activity during the exam.
                Any suspicious behavior will be recorded and may result in warnings or exam termination.
              </Typography>
            </Alert>

            {/* Start Button - Large and Centered */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleStartExam}
                disabled={!webcamPermission || !micPermission}
                sx={{
                  fontSize: '1.2rem',
                  py: 2,
                  px: 6,
                  bgcolor: webcamPermission && micPermission ? '#4caf50' : '#9e9e9e',
                  '&:hover': {
                    bgcolor: webcamPermission && micPermission ? '#45a049' : '#9e9e9e'
                  },
                  fontWeight: 'bold',
                  cursor: webcamPermission && micPermission ? 'pointer' : 'not-allowed'
                }}
              >
                START EXAM
              </Button>
            </Box>

            {/* Permission Status Message and Grant Button */}
            {(!webcamPermission || !micPermission) && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 'bold', mb: 2 }}>
                  {!webcamPermission && !micPermission
                    ? '📷 🎤 Camera and microphone permissions required to start exam'
                    : !webcamPermission
                    ? '📷 Camera permission required to start exam'
                    : '🎤 Microphone permission required to start exam'
                  }
                </Typography>
                <Button
                  variant="contained"
                  onClick={requestPermissions}
                  sx={{
                    bgcolor: '#2196f3',
                    '&:hover': { bgcolor: '#1976d2' },
                    fontWeight: 'bold',
                    px: 4,
                    py: 1
                  }}
                >
                  Grant Permissions
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Exam Header - Blue like proctor app */}
      <Paper sx={{
        p: 1.5,
        mb: 0.5,
        flexShrink: 0,
        bgcolor: '#1976d2',
        color: 'white',
        borderRadius: 0
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', color: 'white', fontWeight: 'bold' }}>
            {examData?.title || 'Exam'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Camera Status */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <VideocamIcon sx={{
                fontSize: '1.2rem',
                color: webcamPermission ? 'white' : '#f44336'  // Red when permission denied
              }} />
              {webcamPermission && examStarted && (
                <CheckCircleIcon sx={{ fontSize: '1rem', color: '#4caf50', ml: 0.3 }} />
              )}
            </Box>

            {/* Microphone Status */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MicIcon sx={{
                fontSize: '1.2rem',
                color: micPermission ? 'white' : '#f44336'  // Red when permission denied
              }} />
              {micPermission && examStarted && (
                <CheckCircleIcon sx={{ fontSize: '1rem', color: '#4caf50', ml: 0.3 }} />
              )}
            </Box>

            {/* Timer */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TimerIcon sx={{ mr: 0.5, fontSize: '1.2rem', color: 'white' }} />
              <Typography variant="h6" sx={{ fontSize: '1rem', color: timeRemaining < 300 ? '#ffeb3b' : 'white', fontWeight: 'bold' }}>
                {formatTime(timeRemaining)}
              </Typography>
            </Box>

            {/* Warning Counter */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon sx={{ mr: 0.5, fontSize: '1.2rem', color: warningCount > 0 ? '#ff9800' : 'white' }} />
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                Warnings: {warningCount}/{maxWarnings}
              </Typography>
            </Box>

            {/* End Exam Button */}
            <Button
              variant="contained"
              size="small"
              sx={{
                bgcolor: '#f44336',
                '&:hover': { bgcolor: '#d32f2f' },
                fontWeight: 'bold',
                color: 'white',
                ml: 2
              }}
              onClick={() => {
                if (examQuestionsRef.current) {
                  examQuestionsRef.current.submitExam();
                }
              }}
            >
              END EXAM
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Exam Content */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'auto', // Allow scrolling
        minHeight: 0, // Important for flex child to shrink
        padding: 1,
        position: 'relative'
      }}>
        {/* Exam Paused Overlay */}
        {examPaused && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            color: 'white'
          }}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
              ⏸️ EXAM PAUSED
            </Typography>
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Restore camera and microphone permissions to continue
            </Typography>
          </Box>
        )}

        <ExamQuestions
          ref={examQuestionsRef}
          examId={examId}
          onSubmit={handleExamSubmission}
          onReturn={handleReturnToDashboard}
          timeRemaining={timeRemaining}
          formatTime={formatTime}
        />
      </Box>

      {/* Hidden Proctoring Components - NOT visible to students like original proctor app */}
      <Box sx={{ display: 'none' }}>
        <WebcamStream
          active={examStarted}
          onAlertReceived={addAlert}
          ref={webcamRef}
        />
        <AudioMonitor
          active={examStarted}
          onAlertReceived={addAlert}
          webcamRef={webcamRef}
          proctorPaused={proctorPaused}
        />
        <AlertDisplay alerts={alerts} />
      </Box>

      {/* Warning Popup - Exactly like original proctor app */}
      <WarningPopup
        open={warningPopupOpen}
        message={warningMessage}
        alertType={currentAlertType}
        warningCount={warningCount}
        maxWarnings={maxWarnings}
        isTerminated={examTerminated}
        onClose={handleWarningPopupClose}
      />

      {/* Exam Termination Dialog */}
      <Dialog
        open={examTerminated}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        onClose={(event, reason) => {
          // Prevent closing via backdrop click or escape key
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            // Allow only programmatic close
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#d32f2f', color: 'white' }}>
          Exam Terminated
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your exam has been terminated due to excessive violations of exam rules.
            Maximum warnings ({maxWarnings}) have been reached.
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {examSubmitted ? (
              <>Your exam has been automatically submitted. You cannot continue the exam after termination.</>
            ) : (
              <>Your exam will be automatically submitted when you click "Return to Dashboard". You cannot continue the exam after termination.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              console.log('🚫 User clicked Return to Dashboard after termination');
              if (!examSubmitted) {
                // Submit the exam with termination status if not already submitted
                if (examQuestionsRef.current) {
                  examQuestionsRef.current.autoSubmitExam();
                }
                // Then return to dashboard
                setTimeout(() => {
                  handleReturnToDashboard();
                }, 1000);
              } else {
                // Already submitted, just return to dashboard
                handleReturnToDashboard();
              }
            }}
            variant="contained"
            color="error"
            fullWidth
            size="large"
          >
            Return to Dashboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission Dialog */}
      <Dialog open={showSubmissionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {submissionResult?.status_type === 'terminated' ? 'Exam Terminated' : 'Exam Submitted Successfully!'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {submissionResult?.status_type === 'terminated'
              ? 'Your exam was terminated due to reaching the maximum number of warnings and has been automatically submitted.'
              : 'Your exam has been submitted successfully.'
            }
          </Typography>
          {submissionResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Exam Statistics:
              </Typography>

              <Box>
                {(() => {
                  // Calculate stats for dialog using the same logic
                  const totalQuestions = submissionResult.total_questions || 0;
                  let attemptedCount = 0;

                  if (submissionResult.questions_with_answers && Array.isArray(submissionResult.questions_with_answers)) {
                    submissionResult.questions_with_answers.forEach(q => {
                      const userAnswer = q.user_answer || q.student_answer || '';
                      if (userAnswer && userAnswer.trim() !== '') {
                        attemptedCount++;
                      }
                    });
                  } else if (submissionResult.answers && typeof submissionResult.answers === 'object') {
                    submissionResult.answers.forEach(answer => {
                      if (answer && answer.trim() !== '') {
                        attemptedCount++;
                      }
                    });
                  }

                  const notAttemptedCount = totalQuestions - attemptedCount;

                  return (
                    <>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Questions attempted:</strong> {attemptedCount} out of {totalQuestions}
                      </Typography>

                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Questions not attempted:</strong> {notAttemptedCount} out of {totalQuestions}
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReturnToDashboard} variant="contained">
            RETURN TO DASHBOARD
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Lost Dialog - Critical Warning */}
      <Dialog
        open={permissionLostDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        disableBackdropClick
      >
        <DialogTitle sx={{ backgroundColor: '#f44336', color: 'white', textAlign: 'center' }}>
          ⚠️ EXAM PAUSED - PERMISSIONS REQUIRED
        </DialogTitle>
        <DialogContent sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#f44336', fontWeight: 'bold' }}>
            Camera and/or Microphone Access Lost!
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Your exam has been paused because camera or microphone permissions were disabled.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            You must restore permissions to continue the exam.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            {/* Camera Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VideocamIcon sx={{
                fontSize: '2rem',
                color: webcamPermission ? '#4caf50' : '#f44336'
              }} />
              <Typography variant="body2" sx={{
                color: webcamPermission ? '#4caf50' : '#f44336',
                fontWeight: 'bold'
              }}>
                Camera: {webcamPermission ? 'ALLOWED' : 'BLOCKED'}
              </Typography>
            </Box>

            {/* Microphone Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MicIcon sx={{
                fontSize: '2rem',
                color: micPermission ? '#4caf50' : '#f44336'
              }} />
              <Typography variant="body2" sx={{
                color: micPermission ? '#4caf50' : '#f44336',
                fontWeight: 'bold'
              }}>
                Microphone: {micPermission ? 'ALLOWED' : 'BLOCKED'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleRestorePermissions}
            variant="contained"
            size="large"
            sx={{
              bgcolor: '#2196f3',
              '&:hover': { bgcolor: '#1976d2' },
              fontWeight: 'bold',
              px: 4,
              py: 1.5
            }}
          >
            RESTORE PERMISSIONS
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegratedExamInterface;
